import { useMutation, useQueryClient } from "@tanstack/react-query";
import { financeApi } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import type { Group, Transaction, TransactionPayload } from "../../lib/types";

export function useRecordEditorMutations({
  group,
  transaction,
  onClose,
  requestAnimatedClose,
  setStatus,
}: {
  group: Group;
  transaction: Transaction | null;
  onClose: () => void;
  requestAnimatedClose: () => void;
  setStatus: (status: string) => void;
}) {
  const queryClient = useQueryClient();
  const invalidateRecords = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.records }),
      queryClient.invalidateQueries({ queryKey: queryKeys.deletedRecords }),
      queryClient.invalidateQueries({ queryKey: queryKeys.summary }),
      queryClient.invalidateQueries({ queryKey: queryKeys.statisticsRoot(group.id) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.statisticsOperationsRoot(group.id) }),
      transaction ? queryClient.invalidateQueries({ queryKey: queryKeys.record(group.id, transaction.id) }) : Promise.resolve(),
    ]);
  };
  const onError = (error: unknown) => setStatus(error instanceof Error ? error.message : "Request failed");

  const saveMutation = useMutation({
    mutationFn: (payload: TransactionPayload) =>
      transaction
        ? financeApi.updateTransaction(group.id, transaction.id, payload)
        : financeApi.createTransaction(group.id, payload),
    onSuccess: async () => {
      await invalidateRecords();
      requestAnimatedClose();
    },
    onError,
  });
  const deleteMutation = useMutation({
    mutationFn: (restoreRecurringDueDate: boolean) =>
      financeApi.deleteTransaction(group.id, transaction!.id, { restoreRecurringDueDate }),
    onSuccess: async () => {
      await invalidateRecords();
      await queryClient.invalidateQueries({ queryKey: queryKeys.recurringPayments(group.id) });
      requestAnimatedClose();
    },
    onError,
  });
  const restoreMutation = useMutation({
    mutationFn: () => financeApi.restoreTransaction(group.id, transaction!.id),
    onSuccess: async () => {
      await invalidateRecords();
      onClose();
    },
    onError,
  });
  const unlinkMutation = useMutation({
    mutationFn: () => financeApi.unlinkTransaction(group.id, transaction!.id),
    onSuccess: async () => {
      await invalidateRecords();
      await queryClient.invalidateQueries({ queryKey: queryKeys.transferLinkSuggestions(group.id) });
      requestAnimatedClose();
    },
    onError,
  });
  const linkMutation = useMutation({
    mutationFn: (partnerId: string) => financeApi.linkTransactions(group.id, transaction!.id, partnerId),
    onSuccess: async () => {
      await invalidateRecords();
      await queryClient.invalidateQueries({ queryKey: queryKeys.transferLinkSuggestions(group.id) });
      requestAnimatedClose();
    },
    onError,
  });

  return { saveMutation, deleteMutation, restoreMutation, unlinkMutation, linkMutation };
}
